import {useSelector, useDispatch} from 'react-redux';
import React, {useMemo} from 'react';
import {useParams} from 'react-router-dom';

// SELECTOR FNS
import {
  hotelDetailsSelector,
  isAltAccoSelector,
  coupleFriendlySelector,
  isGoStaysSelector,
  isDetailsSeoSelector,
  searchRoomDataSelector,
} from '@stencil/hotels-cg-reducer/src/details/selectors';
import {
  isInternationalSelector,
  mobConfigJSONSelectorFn,
} from '@stencil/hotels-common-reducer/src/selectors';

// REFACTOR
import {detailsNavigationIdsAndValues} from '@stencil/hotels-utils/src/get-details-navigation-id-and-values';
import {scrollToEl} from '@stencil/hotels-utils/src/scroll-into-view-details';
import {getDetailSeoPageUrl} from '@stencil/hotels-utils/src/seo-utils';

// COMPONENTS
import {HiddenElement} from '../../../../../../stories/components/CommonStyleUtils/dwebCommon.styles';
import HotelAddressText from '../../../../../../stories/components/hotels/DetailsPageUnif/HotelInfoUI/HotelAddressText';
import HotelName from '../../../../../../stories/components/hotels/DetailsPageUnif/HotelInfoUI/HotelName';
import HotelRatingStars from '../../../../../../stories/components/hotels/DetailsPageUnif/HotelInfoUI/HotelRatingStars';
import GoStaysTag from '../../../../../../stories/components/hotels/common/GoStaysTag/GoStaysTag';
import PersuasionHoverText from '../../../../../../stories/components/hotels/SRP/PersuasionHoverText/PersuasionHoverText';
import TriggerAnchorText from '../../../../../components/TriggerActionText';
import ReviewInfo from '../ReviewInfo/ReviewInfo';

// STYLES
import {defaultTheme} from '@stencil/build-theme';
import * as S from './HotelInfo.styles';

import {omnitureThunk, omnitureConfig} from '@stencil/omniture-analytics/src';

import {DetailsPageComponents} from '@stencil/omniture-analytics/src/constants/details';
import {BookHotelDiv, ViewRoomOptionsButton} from '../HotelDetailsMain';
import Spotlight from '@stencil/gi-components/src/Spotlight/Spotlight';

export default function HotelInfo({
  isSeoDetailPage = false,
  isDetailReviews = false,
  handleBookNowClick = () => {},
  indianness = {},
}) {
  const params = useParams();
  const isGostay = useSelector(isGoStaysSelector);
  const coupleFriendlyData = useSelector(coupleFriendlySelector);
  const hotelDetails = useSelector(hotelDetailsSelector);
  const searchRoomData = useSelector(searchRoomDataSelector);
  const mobConfigData = useSelector(mobConfigJSONSelectorFn);
  const isAltAcco = useSelector(isAltAccoSelector);
  const isInternational = useSelector(isInternationalSelector);
  const isSeoUnif = useSelector(isDetailsSeoSelector);

  const getCoupleFriendlyData = useMemo(() => {
    return [
      {
        ic: coupleFriendlyData?.iconName,
        pt: coupleFriendlyData?.persuasionText,
        htd: {
          ic: coupleFriendlyData?.iconName,
          h: coupleFriendlyData?.hoverData?.heading,
          ht: coupleFriendlyData?.hoverData?.hoverTextList,
          hec: {
            tc: coupleFriendlyData?.hoverData?.headingColour?.textColour,
            bgc: coupleFriendlyData?.hoverData?.headingColour?.backgroundColour,
          },
          hoc: {
            tc: coupleFriendlyData?.hoverData?.hoverColour?.textColour,
            bgc: coupleFriendlyData?.hoverData?.hoverColour?.backgroundColour,
          },
        },
        ccd: {
          tc: coupleFriendlyData?.colourCodeData?.textColour,
          bgc: coupleFriendlyData?.colourCodeData?.backgroundColour,
        },
      },
    ];
  }, [coupleFriendlyData]);

  const seoUrl = `${__ENV_CONFIG__.__GOIBIBO__}${getDetailSeoPageUrl('details', params)}`;

  const dispatch = useDispatch();
  const isHeader = isGostay || hotelDetails?.starRating || coupleFriendlyData;

  return (
    <S.Wrapper expand={isSeoUnif && !isSeoDetailPage}>
      <S.CenterDiv fixedHeight={isHeader}>
        {hotelDetails?.starRating ? (
          <S.RatingOuterWrapper>
            {isAltAcco ? (
              <HotelRatingStars
                hotelRating={hotelDetails?.starRating}
                isAltAcco={isAltAcco}
                isInternational={isInternational}
              />
            ) : (
              <S.StarRatingContainer>
                {hotelDetails?.starRating}
                <HotelRatingStars
                  hotelRating={1}
                  isAltAcco={isAltAcco}
                  isInternational={isInternational}
                />
                <S.Dot>&#183;</S.Dot>
                Hotel
              </S.StarRatingContainer>
            )}
          </S.RatingOuterWrapper>
        ) : null}
        {!!isGostay && <GoStaysTag margin={'0 15px 0px 0'} ctaText="Learn More" forDetailsPage />}
        {coupleFriendlyData?.persuasionText && (
          <S.CoupleFriendlyTagContainer>
            <PersuasionHoverText
              data={getCoupleFriendlyData}
              defaultIcon={'tCoupleFriendly'}
              type="Couple Friendly Persuasion"
              persuasionTitleStyles={{
                border: `1px solid ${defaultTheme.colors.goBrandOrange.base}`,
              }}
            />
          </S.CoupleFriendlyTagContainer>
        )}
        {!!indianness?.cardInfo?.iconURL && (
          <S.IndiannessIcon src={indianness?.cardInfo?.iconURL} />
        )}
      </S.CenterDiv>

      <S.HotelNameAndAddressAndReviewWrapper>
        <S.HotelNameAndAddressContainer>
          <S.CenterDiv>
            <S.SpotlightContainer>
              <Spotlight
                spotlightApplicable={searchRoomData?.spotlightApplicable}
                spotlightInfo={mobConfigData?.spotlightInfo}
              />
            </S.SpotlightContainer>
            <HotelName hotelName={hotelDetails?.name} isUnif={true} isSeoUnif={isSeoUnif} />
          </S.CenterDiv>
          {isSeoDetailPage && seoUrl && <HiddenElement itemProp="url">{seoUrl}</HiddenElement>}

          <S.CenterDiv>
            <TriggerAnchorText
              onClick={() => {
                dispatch(
                  omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
                    cta: {
                      name: `${DetailsPageComponents.HotelAddress}_Clicked`,
                      type: omnitureConfig.ctaType.Button,
                      componentName: DetailsPageComponents.HotelAddress,
                      destination: '',
                    },
                  }),
                );
                scrollToEl(detailsNavigationIdsAndValues.location.id);
              }}
              styleProps={{
                width: '100%',
              }}
            >
              <HotelAddressText
                address={hotelDetails?.address}
                locationPersuasion={hotelDetails?.locationPersuasion}
                pinCode={hotelDetails?.pinCode}
                primaryArea={hotelDetails?.primaryArea}
                locName={hotelDetails?.locationDetail?.name}
                hotelCountry={hotelDetails?.locationDetail?.countryName}
              />
            </TriggerAnchorText>
          </S.CenterDiv>
        </S.HotelNameAndAddressContainer>
        <S.ReviewInfoContainer isAltAcco={isAltAcco}>
          {(!isSeoUnif || isSeoDetailPage || isDetailReviews) && <ReviewInfo isSeo={isSeoUnif} />}
          {isSeoUnif && !isSeoDetailPage ? (
            <ViewRoomOptionsButton onClick={handleBookNowClick}>
              <BookHotelDiv>Book Hotel</BookHotelDiv>
            </ViewRoomOptionsButton>
          ) : null}
        </S.ReviewInfoContainer>
      </S.HotelNameAndAddressAndReviewWrapper>
    </S.Wrapper>
  );
}
